import { ChangeDetectionStrategy, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NotificationIdentifier, UiResponsiveService } from '@evorait/pace-core';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import Popper, { PopperOptions } from 'popper.js';
import { Observable, Subject } from 'rxjs';
import { map, mergeMap, share, take, takeUntil } from 'rxjs/operators';
import { ConfirmationInstanceToken, ConfirmationInterface } from '../../core/interfaces/confirmation.interface';
import { addOrUpdateParticipantsfromConfirmation, selectParticipant } from '../../store/actions/form.actions';
import {
    getFormMaster,
    getFormNotification,
    getFormParticipantCopies,
    getFormSelectedParticipantCopy,
    getFormSelectedParticipantCopyId,
} from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { FormsConstants } from '../../types/forms-constants';
import { ParticipantCopy, ParticipantCopyId } from '../../types/participant-copy';

@Component({
    selector: 'pace-select-participant-copy',
    templateUrl: './select-participant-copy.component.html',
    styleUrls: ['./select-participant-copy.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectParticipantCopyComponent implements OnInit, OnDestroy {
    @ViewChild('participantCopiesButton')
    public filterButton: ElementRef;

    @ViewChild('participantCopiesDropupMenu')
    public dropupMenu: ElementRef;

    public formMaster$: Observable<ParticipantCopy>;
    public participantCopies$: Observable<ParticipantCopy[]>;
    public selectedParticipantCopyId$: Observable<ParticipantCopyId>;
    public isParticipantSelected: Observable<boolean>;
    public selectedParticipantCopy$: Observable<ParticipantCopy>;
    public notification$: Observable<NotificationIdentifier>;

    // popper.js related variables to display and control the dropup menu
    private popper: Popper | undefined;
    private readonly popperConfig: PopperOptions = {
        placement: 'top',
    };
    public showDropupMenu = false;

    public faUser = faUser;
    public isMobile: boolean;

    private unsubscribeNow = new Subject<void>();
    public constructor(
        private readonly store: Store<FormsState>,
        @Inject(ConfirmationInstanceToken) private readonly confirmationService: ConfirmationInterface,
        private readonly responsiveService: UiResponsiveService,
    ) {
        this.notification$ = this.store.select(getFormNotification);
        this.formMaster$ = store.select(getFormMaster);
        this.participantCopies$ = store
            .select(getFormParticipantCopies)
            .pipe(
                map((participantCopies) =>
                    Object.values(participantCopies).filter((participantCopy) => participantCopy.hasCopy),
                ),
            );
        this.selectedParticipantCopyId$ = store.select(getFormSelectedParticipantCopyId);
        this.isParticipantSelected = this.selectedParticipantCopyId$.pipe(
            map((selectedParticipant) => selectedParticipant !== FormsConstants.MASTER_PARTICIPANT_ID),
            share(),
        );
        this.selectedParticipantCopy$ = store.select(getFormSelectedParticipantCopy).pipe(share());
    }

    public ngOnInit(): void {
        // @teo can we get rid of that pipe?
        this.notification$
            .pipe(
                mergeMap((notificationId) => this.confirmationService.getParticipantsFromAttendees(notificationId)),
                take(1),
            )
            .subscribe((confirmationParticipants) => {
                this.store.dispatch(
                    addOrUpdateParticipantsfromConfirmation({
                        participants: confirmationParticipants,
                    }),
                );
            });
        this.responsiveService.isMobile$.pipe(takeUntil(this.unsubscribeNow)).subscribe((isMobile) => {
            this.isMobile = isMobile;
        });
    }

    public ngOnDestroy(): void {
        this.unsubscribeNow.next();
        this.unsubscribeNow.complete();
    }

    public toggleDropup(event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        this.showDropupMenu = !this.showDropupMenu;
        if (this.showDropupMenu && this.filterButton && this.dropupMenu) {
            this.popper = new Popper(
                this.filterButton.nativeElement as HTMLElement,
                this.dropupMenu.nativeElement as HTMLElement,
                this.popperConfig,
            );
            this.popper.scheduleUpdate();
        } else if (typeof this.popper !== 'undefined') {
            this.popper.destroy();
            this.popper = undefined;
        }
    }

    public onSelected(participantId: ParticipantCopyId): void {
        this.toggleDropup();
        this.store.dispatch(selectParticipant({ id: participantId }));
    }

    public getLabel(participant: ParticipantCopy): string {
        return participant.id === FormsConstants.MASTER_PARTICIPANT_ID
            ? 'Master Checklist'
            : `${participant.firstName} ${participant.lastName}`;
    }
}
